import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Plus, UserPlus, Edit, Copy, Users, ChevronDown, ChevronRight, User, FileText } from 'lucide-react'
import { RoleWithMember, Team } from '../../services/orgChartService'

/** Fixed card footprint — matches OrgChartView CARD_WIDTH (w-72). */
const CARD_SIZE_CLASS = 'w-72 h-80'

interface RoleCardProps {
  role: RoleWithMember
  teams: Team[]
  isAdmin: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
  onEdit?: () => void
  onCopyRole?: () => void
  onAssignPerson?: () => void
  onAddColleague?: () => void
  onAddDirectReport?: () => void
  onFocusSubtree?: () => void
  onViewPositionDescription?: () => void
}

export const RoleCard: React.FC<RoleCardProps> = ({
  role,
  teams: _teams,
  isAdmin,
  isExpanded = false,
  onToggleExpand,
  onEdit,
  onCopyRole,
  onAssignPerson,
  onAddColleague,
  onAddDirectReport,
  onFocusSubtree,
  onViewPositionDescription
}) => {
  const [photoFailed, setPhotoFailed] = useState(false)
  const [accountabilityTooltip, setAccountabilityTooltip] = useState<{
    top: number
    left: number
  } | null>(null)
  const moreAccountabilitiesRef = useRef<HTMLButtonElement>(null)
  const assignClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const photoUrl = role.assignedMember?.thumbnailPhotoUrl || null
  const showPhoto = !!photoUrl && !photoFailed

  useEffect(() => {
    setPhotoFailed(false)
  }, [photoUrl])

  useEffect(() => {
    return () => {
      if (assignClickTimer.current) clearTimeout(assignClickTimer.current)
    }
  }, [])

  const accountabilities = role.accountabilities || []
  const hasHiddenAccountabilities = accountabilities.length > 3
  const visibleAccountabilities = hasHiddenAccountabilities
    ? accountabilities.slice(0, 2)
    : accountabilities.slice(0, 3)
  const hiddenAccountabilityCount = hasHiddenAccountabilities
    ? accountabilities.length - visibleAccountabilities.length
    : 0

  const showAccountabilityTooltip = () => {
    const el = moreAccountabilitiesRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setAccountabilityTooltip({
      top: rect.top - 8,
      left: rect.left
    })
  }

  const hideAccountabilityTooltip = () => {
    setAccountabilityTooltip(null)
  }

  const handleAssignClick = () => {
    if (!isAdmin || !onAssignPerson) return
    if (assignClickTimer.current) clearTimeout(assignClickTimer.current)
    // Delay so a double-click can cancel and focus the subtree instead
    assignClickTimer.current = setTimeout(() => {
      onAssignPerson()
      assignClickTimer.current = null
    }, 250)
  }

  const handleFocusDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (assignClickTimer.current) {
      clearTimeout(assignClickTimer.current)
      assignClickTimer.current = null
    }
    onFocusSubtree?.()
  }

  // Get effective teams (direct or inherited - no distinction shown)
  const directTeams = role.teams || []
  const directTeamColors = role.teamColors || []
  const inheritedTeams = role.inheritedTeams || []
  const inheritedTeamColors = role.inheritedTeamColors || []
  
  const effectiveTeams = directTeams.length > 0 ? directTeams : inheritedTeams
  const effectiveTeamColors = directTeams.length > 0 ? directTeamColors : inheritedTeamColors
  const isVacant = !role.assignedMember
  const isContractor = !!role.isContractor

  // Use first team color for left border
  const primaryTeamColor = effectiveTeamColors[0] || null

  return (
    <div 
      className={`${CARD_SIZE_CLASS} flex flex-col overflow-hidden rounded-lg border-2 shadow-sm transition-shadow hover:shadow-md ${
        isContractor ? 'bg-sky-50' : 'bg-white'
      } ${
        isVacant
          ? isContractor
            ? 'border-dashed border-sky-300'
            : 'border-dashed border-gray-300'
          : isContractor
            ? 'border-sky-200'
            : 'border-gray-200'
      }`}
      style={primaryTeamColor ? { borderLeftColor: primaryTeamColor, borderLeftWidth: '4px' } : {}}
      onDoubleClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onFocusSubtree?.()
      }}
    >
      <div className="flex-1 min-h-0 p-4 flex flex-col overflow-hidden">
        {/* Header with team / contractor badges + actions */}
        <div className="flex items-start justify-between gap-2 mb-3 flex-shrink-0">
          <div className="flex flex-wrap gap-1 min-w-0 flex-1 content-start">
            {effectiveTeams.map((teamName, idx) => (
              <span 
                key={teamName}
                className="px-2 py-0.5 text-xs rounded-full truncate max-w-[7rem]"
                style={{ 
                  backgroundColor: effectiveTeamColors[idx] ? `${effectiveTeamColors[idx]}20` : '#f3f4f6',
                  color: effectiveTeamColors[idx] || '#6b7280'
                }}
              >
                {teamName}
              </span>
            ))}
            {isContractor && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-sky-200 text-sky-900 font-medium">
                Contractor
              </span>
            )}
          </div>
          
          <div
            className="flex items-center space-x-1 flex-shrink-0"
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onViewPositionDescription}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="Position description"
            >
              <FileText className="w-4 h-4" />
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={onCopyRole}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title="Copy role"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={onEdit}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title="Edit role"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Person / Vacant */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          {isVacant ? (
            <div 
              className={`w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center ${
                isAdmin ? 'cursor-pointer hover:bg-gray-300 transition-colors' : ''
              }`}
              onClick={isAdmin ? handleAssignClick : undefined}
              onDoubleClick={handleFocusDoubleClick}
              title={isAdmin ? 'Assign person to this role' : undefined}
            >
              {isAdmin ? (
                <UserPlus className="w-6 h-6 text-gray-500" />
              ) : (
                <span className="text-gray-500 font-medium">V</span>
              )}
            </div>
          ) : isAdmin ? (
            <button
              type="button"
              onClick={handleAssignClick}
              onDoubleClick={handleFocusDoubleClick}
              title="Change who holds this role"
              className="flex-shrink-0 rounded-full cursor-pointer ring-offset-2 hover:ring-2 hover:ring-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {showPhoto ? (
                <img
                  src={photoUrl}
                  alt={role.assignedMember?.name.fullName}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-500" />
                </div>
              )}
            </button>
          ) : showPhoto ? (
            <img
              src={photoUrl}
              alt={role.assignedMember?.name.fullName}
              className="w-12 h-12 rounded-full object-cover"
              onError={() => setPhotoFailed(true)}
              onDoubleClick={handleFocusDoubleClick}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center"
              onDoubleClick={handleFocusDoubleClick}
            >
              <User className="w-6 h-6 text-gray-500" />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 line-clamp-2 leading-snug">{role.title}</h3>
            {isVacant ? (
              <p className="text-sm text-gray-400 italic">Vacant</p>
            ) : isAdmin ? (
              <button
                type="button"
                onClick={handleAssignClick}
                onDoubleClick={handleFocusDoubleClick}
                title="Change who holds this role"
                className="block w-full text-left min-w-0 rounded cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <p className="text-sm text-gray-700 truncate">{role.assignedMember?.name.fullName}</p>
                <p className="text-xs text-gray-500 truncate">{role.assignedMember?.primaryEmail}</p>
              </button>
            ) : (
              <div onDoubleClick={handleFocusDoubleClick}>
                <p className="text-sm text-gray-700 truncate">{role.assignedMember?.name.fullName}</p>
                <p className="text-xs text-gray-500 truncate">{role.assignedMember?.primaryEmail}</p>
              </div>
            )}
          </div>
        </div>

        {/* Direct reports — always reserve space */}
        <div className="h-8 mt-3 flex-shrink-0 flex items-center">
          {role.directReportsCount > 0 && onToggleExpand ? (
            <button
              onClick={onToggleExpand}
              onDoubleClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <Users className="w-4 h-4" />
              <span>{role.directReportsCount} direct report{role.directReportsCount !== 1 ? 's' : ''}</span>
            </button>
          ) : null}
        </div>

        {/* Accountabilities — fixed remaining space; +more always visible when truncated */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex-1 min-h-0 flex flex-col overflow-hidden">
          {accountabilities.length > 0 ? (
            <>
              <p className="text-xs font-medium text-gray-500 mb-1 flex-shrink-0">Accountabilities</p>
              <ul className="text-xs text-gray-600 space-y-1 flex-1 min-h-0 overflow-hidden">
                {visibleAccountabilities.map((acc, idx) => (
                  <li key={idx} className="truncate">• {acc}</li>
                ))}
              </ul>
              {hasHiddenAccountabilities && (
                <button
                  ref={moreAccountabilitiesRef}
                  type="button"
                  className="flex-shrink-0 mt-1 text-left text-xs text-gray-400 hover:text-purple-600 transition-colors"
                  onMouseEnter={showAccountabilityTooltip}
                  onMouseLeave={hideAccountabilityTooltip}
                  onFocus={showAccountabilityTooltip}
                  onBlur={hideAccountabilityTooltip}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  +{hiddenAccountabilityCount} more
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {accountabilityTooltip &&
        createPortal(
          <div
            className="fixed z-[100] max-w-xs rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg pointer-events-none"
            style={{
              top: accountabilityTooltip.top,
              left: accountabilityTooltip.left,
              transform: 'translateY(-100%)'
            }}
            role="tooltip"
          >
            <p className="text-xs font-medium text-gray-500 mb-1">Accountabilities</p>
            <ul className="text-xs text-gray-700 space-y-1">
              {accountabilities.map((acc, idx) => (
                <li key={idx}>• {acc}</li>
              ))}
            </ul>
          </div>,
          document.body
        )}

      {/* Footer — always same height so admin/non-admin cards match */}
      <div
        className="h-9 flex-shrink-0 flex border-t border-gray-100"
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {isAdmin ? (
          <>
            <button
              onClick={onAddColleague}
              className="flex-1 flex items-center justify-center space-x-1 px-3 text-xs text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              title="Add colleague at same level"
            >
              <Plus className="w-3 h-3" />
              <span>Colleague</span>
            </button>
            <div className="w-px bg-gray-100" />
            <button
              onClick={onAddDirectReport}
              className="flex-1 flex items-center justify-center space-x-1 px-3 text-xs text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              title="Add direct report"
            >
              <Plus className="w-3 h-3" />
              <span>Report</span>
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}
